<template>
  <a
    class="card"
    :class="[
      `card--${backgroundColor}`,
      {
        'card--sizes-panel-open': sizesPanelIsOpen,
        'is-selected': (isCardSelected && isHoverStateEnabled) || isHoverStateForced,
      },
    ]"
    :href="productUrl"
    :target="target"
    @click="handleClick"
    @mouseover="selectCard(true)"
    @mouseleave="selectCard(false)"
    v-data-test-id="'card'"
  >
    <CardVisualBox
      :currentVariant="sortedCurrentVariantWithoutVideos"
      :isShowingBackground="isShowingBackground"
      :isCardSelected="isCardSelected"
      :isQuickAddEnabled="isQuickAddEnabled"
      :backgroundColor="visualBackgroundColor"
      :badgeBackgroundColor="badgeBackgroundColor"
      :card="card"
      :imageFormat="imageFormat"
      :isGiftCardProduct="isGiftCardProduct"
      :context="context"
      :target="target"
      v-data-test-id="'card-visualbox'"
    />
    <div class="card__bottom-box" :class="{ 'card__bottom-box--all-gender': genderToShow === '' }">
      <transition name="fade" mode="out-in">
        <div
          v-if="(isCardSelected && isHoverStateEnabled && !isMobile) || isHoverStateForced"
          class="card__variant-sliders"
        >
          <VariantSlider
            v-for="(variants, gender, index) in sliders"
            :key="`slider-box-${index}`"
            :currentVariantId="currentVariantId"
            :variants="variants"
            :label="gender"
            :isNavigationMode="true"
            :slidesPerPage="6"
            @slide-click="onCurrentVariantChange"
            v-data-test-id="'variant-slider'"
          />
        </div>
        <CardDescription
          v-else
          :backgroundColor="descriptionBackgroundColor"
          :card="card"
          :isShowingBackground="isShowingBackground"
          :isGiftCardProduct="isGiftCardProduct"
          :productName="card.name"
          v-data-test-id="'card-description'"
        />
      </transition>
    </div>
  </a>
</template>

<script>
import { cloneDeep, groupBy } from 'lodash-es'
import { mapState, mapGetters, mapActions } from 'vuex'
import { BADGE_BACKGROUND_COLORS } from '@/config/constants'
import { CARD_COLORS, CARD_BACKGROUND_COLORS } from '@/config/constants'
import { getPathFromUrl } from '@/helpers/url.js'
import { GENDERS, PRODUCT_TYPES } from '@/stores/product'
import CardVisualBox from '@/components/_shared/Cards/CardVisualBox.vue'
import CardDescription from '@/components/_shared/Cards/CardDescription.vue'
import { CONTENTFUL_IMAGE_FORMAT } from '@/components/_shared/ImageContentful.vue'
import VariantSlider from '@/components/_shared/VariantSliders/VariantSlider.vue'

const PRODUCT_PAGE_LINK_ELEMENTS = [
  'card-visual-box__image-wrapper',
  'card-visual-box__image',
  'card-carousel__image',
  'card-carousel__item',
  'card-button',
]

function getGenderValues(variant) {
  return variant?.filterValues.gender || variant?.gender || []
}

export default {
  name: 'Card',
  components: { CardVisualBox, CardDescription, VariantSlider },

  props: {
    backgroundColor: {
      type: String,
      validator(value) {
        return Object.values(CARD_COLORS).includes(value)
      },
      default: CARD_COLORS.light,
    },

    card: {
      type: Object,
      required: true,
    },

    context: {
      type: String,
    },

    genderToShow: {
      type: String,
      default: '',
    },

    imageFormat: {
      type: String,
      default: CONTENTFUL_IMAGE_FORMAT.jpg,
    },

    index: {
      type: Number,
      default: 0,
    },

    isHoverStateEnabled: {
      type: Boolean,
      default: true,
    },

    isHoverStateForced: {
      type: Boolean,
    },

    isQuickViewEnabled: {
      type: Boolean,
      default: true,
    },

    isQuickAddEnabled: {
      type: Boolean,
      default: true,
    },

    target: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      currentVariantId: null,
      isCardSelected: false,
      isHover: false,
      // TODO: Remove once DY-test ended
      // https://adm.dynamicyield.com/r?locale=en&redirectToSectionId=8772797#/smarts/api/496336
      hasCampaignViewBeenReported: false,
      sizesPanelIsOpen: false,
      CONTENTFUL_IMAGE_FORMAT,
    }
  },

  computed: {
    ...mapState({
      currency: state => state.store.currency,
      currentAdditionalNav: state => state.nav.currentAdditionalNav,
    }),

    ...mapGetters({
      isMqLarge: 'viewport/isMqLarge',
      isFeatureEnabled: 'featureToggle/isEnabled',
    }),

    isCardGalleryTest() {
      return this.isFeatureEnabled('cardGalleryTest')
    },

    isQuickViewActive() {
      return this.isQuickViewEnabled && this.isMobile
    },

    badgeBackgroundColor() {
      const matches = {
        [CARD_COLORS.light]: BADGE_BACKGROUND_COLORS.white,
        [CARD_COLORS.ghost]: BADGE_BACKGROUND_COLORS.white,
      }
      return matches[this.backgroundColor]
    },

    visualBackgroundColor() {
      const matches = {
        [CARD_COLORS.light]: CARD_BACKGROUND_COLORS.light,
        [CARD_COLORS.ghost]: CARD_BACKGROUND_COLORS.ghost,
      }
      return matches[this.backgroundColor]
    },

    descriptionBackgroundColor() {
      const matches = {
        [CARD_COLORS.light]: CARD_BACKGROUND_COLORS.white,
        [CARD_COLORS.ghost]: CARD_BACKGROUND_COLORS.transparent,
      }
      return matches[this.backgroundColor]
    },

    currentVariant() {
      const { card = {} } = this
      const variantId = this.currentVariantId || card.displayedVariantId
      return (
        card.variants.find(
          variant => variant.id === variantId && this.hasVariantGenderToShow(variant)
        ) ||
        card.variants.find(variant => this.hasVariantGenderToShow(variant)) ||
        card.variants.find(variant => variant.id === variantId) ||
        {}
      )
    },

    sortedCurrentVariantWithoutVideos() {
      const currentVariant = cloneDeep(this.currentVariant)
      if (this.currentVariant?.productVariantData) {
        // Move editorial images to the end of the image set while preserving the order of the other images
        currentVariant.productVariantData.assets.sort(
          (a, b) => a.isEditorialImage - b.isEditorialImage
        )
      }
      return currentVariant
    },

    isGiftCardProduct() {
      return this.currentVariant?.spreeProduct?.productType === PRODUCT_TYPES.giftCards
    },

    isMobile() {
      return !this.isMqLarge
    },

    isShowingBackground() {
      return (
        this.isCardSelected &&
        !this.colorSelectorIsHovered &&
        !this.isMobile &&
        (this.backgroundImageUrl || this.backgroundVideoUrl)
      )
    },

    productUrl() {
      const url = this.currentVariant?.productUrl || ''
      let params = ''

      if (window.document.body.classList.contains('iframe-layout')) {
        const sep = url.indexOf('?') > -1 ? '&' : '?'
        params = `${sep}iframe=true`
      }

      return url + params
    },

    isInsideSearch() {
      return this.currentAdditionalNav === 'search'
    },

    sliders() {
      return groupBy(this.card.variants, variant => {
        const genders = getGenderValues(variant)
        return genders.length > 1 ? GENDERS.unisex : genders[0]
      })
    },
  },

  created() {
    this.currentVariantId = this.currentVariant.id
  },

  methods: {
    ...mapActions({
      showQuickViewOverlay: 'overlay/showQuickViewOverlay',
    }),

    onSizesPanelChange(status) {
      this.sizesPanelIsOpen = status
    },

    hasVariantGenderToShow(variant) {
      return !this.genderToShow || getGenderValues(variant).includes(this.genderToShow)
    },

    handleClick(event) {
      const { card } = this
      this.reportClickToDy()

      if (this.isQuickViewActive && !this.isGiftCardProduct) {
        event.preventDefault()
        this.$emit('open-quickview')
        const productSlug = this.card.variants[0].spreeProduct.groupSlug
        this.showQuickViewOverlay({
          productSlug,
          genderToShow: this.genderToShow,
          sku: Number(this.currentVariant.spreeProduct.sku),
        })
        return this.trackProductListView()
      }

      const isProductPageVisit = [...event.target.classList].some(singleClass => {
        return PRODUCT_PAGE_LINK_ELEMENTS.includes(singleClass)
      })

      isProductPageVisit ? this.trackProductListClick() : event.preventDefault()
      this.$emit('click', card)
    },

    selectCard(isSelected) {
      if (this.isInsideSearch || this.isCardSelected === isSelected) return

      this.isCardSelected = isSelected

      // TODO: Remove once DY-test ended
      // https://adm.dynamicyield.com/r?locale=en&redirectToSectionId=8772797#/smarts/api/496336
      if (!this.hasCampaignViewBeenReported && this.isCardGalleryTest) {
        this.reportViewToDy()
      }
    },

    reportViewToDy() {
      this.hasCampaignViewBeenReported = true
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignView,
        data: { identifier: `card-carousel-${this.index}` },
      })
    },

    reportClickToDy() {
      if (!this.card?.slotId) return
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignClick,
        campaignIds: {
          slotId: this.card.slotId,
        },
        data: { identifier: `card-carousel-${this.index}` },
      })
    },

    onCurrentVariantChange({ currentVariantId }) {
      this.currentVariantId = currentVariantId
      this.trackProductListView()
    },

    /* For bigger screens - goes to PDP */
    trackProductListClick() {
      const productData = {
        items: {
          id: this.currentVariant?.spreeProduct?.sku,
          price: this.card.price,
          position: this.index + 1,
        },
        currency: document.body.dataset.currency,
        list: this.context || getPathFromUrl(window.location.href),
      }

      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.listClick,
        productData,
      })
    },

    /* For mobile - opens quickView / For desktop - on variant update */
    trackProductListView() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.listView,
        productData: {
          viewmode: 'quickview',
          items: [
            {
              id: this.currentVariant?.spreeProduct?.sku,
              price: this.card.price,
              list: this.context || getPathFromUrl(window.location.href),
              position: this.index + 1,
            },
          ],
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import './Card';
</style>
